import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { QuotaService } from '../../../quotas/services/quota.service';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';
import { ApplicationItem } from '../../../quotas/models/application.interface';

export interface VrmsxDialogData {
  application: ApplicationItem;
}

import { splitApiErrors } from '../../../../core/utils/api-format';

@Component({
  selector: 'app-vrmsx-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule
  ],
  templateUrl: './vrmsx-dialog.component.html',
  styleUrls: ['./vrmsx-dialog.component.css']
})
export class VrmsxDialogComponent {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<VrmsxDialogComponent>);
  private snackBar = inject(MatSnackBar);
  data = inject<VrmsxDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');
  fieldErrors = signal<Record<string, string>>({});

  organizationInn = '';
  personIdnp = '';
  veterinaryDocumentStatus = 'YES';
  reason = '';

  getFieldError(field: string): string {
    return this.fieldErrors()[field] || '';
  }

  clearFieldError(field: string): void {
    const errors = { ...this.fieldErrors() };
    delete errors[field];
    this.fieldErrors.set(errors);
  }

  onNumericInput(event: Event, field: 'personIdnp' | 'organizationInn'): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '');
    input.value = filtered;
    this[field] = filtered;
    this.clearFieldError(field);
  }

  isFormValid(): boolean {
    return !!(this.organizationInn && this.personIdnp && this.veterinaryDocumentStatus);
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');

    // Открываем диалог OEP подписи
    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: 'Подпись для ВРМСХ'
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.updateVeterinaryStatus(result.token);
      } else if (result?.fieldErrors) {
        this.fieldErrors.set(result.fieldErrors);
      }
    });
  }

  private updateVeterinaryStatus(token: string): void {
    this.loading.set(true);

    this.quotaService.updateVeterinaryDocumentStatus({
      applicationId: this.data.application.id,
      organizationInn: this.organizationInn,
      personIdnp: this.personIdnp,
      reason: this.reason,
      token: token,
      veterinaryDocumentStatus: this.veterinaryDocumentStatus
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open('ВРМСХ успешно обновлен', 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.handleApiErrors(response);
        }
      },
      error: (error) => {
        console.error('Error updating VRMSX:', error);
        if (error.error && Array.isArray(error.error.res)) {
          this.handleApiErrors(error.error);
        } else {
          this.errorMessage.set('Ошибка обновления ВРМСХ');
        }
        this.loading.set(false);
      }
    });
  }

  private handleApiErrors(response: { msg?: string; res?: Array<{ field: string; ru: string }> }): void {
    const { fieldErrors, message } = splitApiErrors(response, 'Ошибка обновления ВРМСХ');
    if (Object.keys(fieldErrors).length) {
      this.fieldErrors.set(fieldErrors);
    }
    if (message) {
      this.errorMessage.set(message);
    }
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }
}
